import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import api from '../lib/api';
import {
  Wallet,
  Shield,
  Bell,
  DollarSign,
  Key,
  Eye,
  EyeOff,
  Check,
  AlertTriangle,
  Loader2,
  ExternalLink,
  ArrowDownToLine,
  RefreshCw
} from 'lucide-react';

export default function Settings() {
  const navigate = useNavigate();
  const { user, connectWallet, disconnectWallet, updateUser, logout } = useAuthStore();
  const [activeTab, setActiveTab] = useState('wallet');
  
  // Wallet form
  const [walletAddress, setWalletAddress] = useState(user?.wallet?.address || '');
  const [proxyAddress, setProxyAddress] = useState(user?.wallet?.proxyAddress || '');
  const [privateKey, setPrivateKey] = useState('');
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [signatureType, setSignatureType] = useState(user?.wallet?.signatureType || 0);
  const [isConnecting, setIsConnecting] = useState(false);
  const [walletError, setWalletError] = useState('');
  const [walletSuccess, setWalletSuccess] = useState('');

  const proxyRequired = signatureType === 2;
  const proxyLabel = proxyRequired ? 'Proxy/Funder Address (Polymarket)' : 'Proxy/Funder Address (optional)';
  
  // Wallet balances & withdraw
  const [walletBalances, setWalletBalances] = useState(null);
  const [isLoadingBalances, setIsLoadingBalances] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [isWithdrawing, setIsWithdrawing] = useState(false);

  // WCOL (Wrapped Collateral) state
  const [wcolBalance, setWcolBalance] = useState(null);
  const [isLoadingWcol, setIsLoadingWcol] = useState(false);
  const [isUnwrappingWcol, setIsUnwrappingWcol] = useState(false);

  // Settings form
  const [settings, setSettings] = useState({
    maxPositionSize: user?.settings?.maxPositionSize || 100,
    maxTotalExposure: user?.settings?.maxTotalExposure || 1000,
    defaultSlippage: user?.settings?.defaultSlippage || 2,
    autoCompound: user?.settings?.autoCompound || false,
    notifications: {
      email: user?.settings?.notifications?.email ?? true,
      browser: user?.settings?.notifications?.browser ?? true,
      trades: user?.settings?.notifications?.trades ?? true,
      stopLoss: user?.settings?.notifications?.stopLoss ?? true
    }
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleConnectWallet = async (e) => {
    e.preventDefault();
    setWalletError('');
    setWalletSuccess('');
    
    if (!walletAddress || !privateKey) {
      setWalletError('Wallet address and private key are required');
      return;
    }
    
    setIsConnecting(true);

    const proxyTrimmed = (proxyAddress || '').trim();
    const result = await connectWallet({
      address: walletAddress,
      ...(proxyTrimmed ? { proxyAddress: proxyTrimmed } : {}),
      privateKey,
      signatureType
    });
    
    if (result.success) {
      setWalletSuccess('Wallet connected successfully!');
      setPrivateKey('');
    } else {
      setWalletError(result.error || 'Failed to connect wallet');
    }
    
    setIsConnecting(false);
  };

  const handleDisconnectWallet = async () => {
    if (!confirm('Are you sure you want to disconnect your wallet?')) return;
    
    const result = await disconnectWallet();
    if (result.success) {
      setWalletAddress('');
      setProxyAddress('');
      setPrivateKey('');
    }
  };

  // Fetch wallet balances
  const fetchWalletBalances = async () => {
    if (!user?.wallet?.isConnected) return;
    setIsLoadingBalances(true);
    try {
      const response = await api.get('/auth/wallet/balances');
      setWalletBalances(response.data);
    } catch (err) {
      console.error('Failed to fetch balances:', err);
    }
    setIsLoadingBalances(false);
  };

  // Fetch WCOL balance
  const fetchWcolBalance = async () => {
    if (!user?.wallet?.isConnected) return;
    setIsLoadingWcol(true);
    try {
      const response = await api.get('/wallet/wcol-balance');
      setWcolBalance(response.data);
    } catch (err) {
      console.error('Failed to fetch WCOL balance:', err);
    }
    setIsLoadingWcol(false);
  };

  // Unwrap WCOL to USDC
  const handleUnwrapWcol = async () => {
    setIsUnwrappingWcol(true);
    setWalletError('');
    try {
      const response = await api.post('/wallet/unwrap-wcol');
      setWalletSuccess(response.data.message || `Unwrapped ${response.data.amount?.toFixed(2)} WCOL to USDC!`);
      // Refresh balances after unwrap
      setTimeout(() => {
        fetchWcolBalance();
        fetchWalletBalances();
      }, 3000);
    } catch (err) {
      setWalletError(err.response?.data?.error || 'Failed to unwrap WCOL');
    }
    setIsUnwrappingWcol(false);
  };

  // Withdraw from proxy
  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) {
      setWalletError('Enter a valid withdrawal amount');
      return;
    }
    if (walletBalances?.proxy?.usdc && amount > walletBalances.proxy.usdc) {
      setWalletError('Amount exceeds proxy USDC balance');
      return;
    }
    
    setIsWithdrawing(true);
    setWalletError('');
    try {
      const response = await api.post('/auth/wallet/withdraw', { amount });
      setWalletSuccess(response.data.message || 'Withdrawal initiated!');
      setWithdrawAmount('');
      // Refresh balances after withdrawal
      setTimeout(fetchWalletBalances, 3000);
    } catch (err) {
      setWalletError(err.response?.data?.error || 'Withdrawal failed');
    }
    setIsWithdrawing(false);
  };

  // Fetch balances when wallet tab is active and wallet is connected
  useEffect(() => {
    if (activeTab === 'wallet' && user?.wallet?.isConnected) {
      fetchWalletBalances();
      fetchWcolBalance();
    }
  }, [activeTab, user?.wallet?.isConnected]);

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      await api.put('/settings', { settings });
      updateUser({ settings });
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const handleChangePassword = async () => {
    if (passwordData.new !== passwordData.confirm) {
      alert('New passwords do not match');
      return;
    }
    if (passwordData.new.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }
    setIsChangingPassword(true);
    try {
      await api.post('/auth/change-password', {
        currentPassword: passwordData.current,
        newPassword: passwordData.new
      });
      alert('Password changed successfully');
      setShowPasswordModal(false);
      setPasswordData({ current: '', new: '', confirm: '' });
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = prompt('Type DELETE to confirm account deletion:');
    if (confirmed !== 'DELETE') {
      return;
    }
    try {
      await api.delete('/auth/account');
      logout();
      navigate('/login');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete account');
    }
  };

  const tabs = [
    { id: 'wallet', label: 'Wallet', icon: Wallet },
    { id: 'trading', label: 'Trading', icon: DollarSign },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield }
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-dark-400">Manage your account and trading preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Tabs */}
        <div className="lg:col-span-1">
          <nav className="space-y-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary-600 text-white'
                    : 'text-dark-300 hover:bg-dark-800'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          {/* Wallet Tab */}
          {activeTab === 'wallet' && (
            <div className="card space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-1">Wallet Connection</h2>
                <p className="text-sm text-dark-400">
                  Connect your Polymarket wallet to enable copy trading
                </p>
              </div>

              {user?.wallet?.isConnected ? (
                <div className="space-y-4">
                  <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                    <div className="flex items-center gap-2 text-emerald-400 mb-3">
                      <Check className="w-5 h-5" />
                      <span className="font-medium">Wallet Connected</span>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs text-dark-400">Funder/Proxy Address:</p>
                        <p className="text-sm font-mono text-dark-300">{user.wallet.proxyAddress || user.wallet.address}</p>
                      </div>
                      <div>
                        <p className="text-xs text-dark-400">Signer Address:</p>
                        <p className="text-sm font-mono text-dark-300">{user.wallet.address}</p>
                      </div>
                    </div>
                  </div>

                  {/* Signature Type Changer */}
                  <div className="p-4 bg-dark-800 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium">Signature Type</label>
                      <span className="text-xs text-dark-400">Current: Type {user.wallet.signatureType ?? 0}</span>
                    </div>
                    <select
                      value={user.wallet.signatureType ?? 0}
                      onChange={async (e) => {
                        const newType = parseInt(e.target.value);
                        if (
                          newType === 2 &&
                          (!user.wallet.proxyAddress || user.wallet.proxyAddress.toLowerCase() === user.wallet.address?.toLowerCase())
                        ) {
                          setWalletError('Type 2 requires a separate proxy (funder) address. Disconnect and reconnect your wallet with the correct Polymarket proxy address.');
                          return;
                        }
                        try {
                          const response = await api.put('/auth/wallet/signature-type', { signatureType: newType });
                          // Use the wallet object returned from server to ensure consistency
                          if (response.data.wallet) {
                            updateUser({ wallet: response.data.wallet });
                          } else {
                            updateUser({ wallet: { ...user.wallet, signatureType: newType } });
                          }
                          setWalletSuccess('Signature type updated!');
                          setTimeout(() => setWalletSuccess(''), 3000);
                        } catch (err) {
                          setWalletError(err.response?.data?.error || 'Failed to update signature type');
                        }
                      }}
                      className="input"
                    >
                      <option value={0}>Type 0 - EOA (Direct Wallet)</option>
                      <option value={1}>Type 1 - Magic/Email Wallet</option>
                      <option value={2}>Type 2 - Polymarket.com Login</option>
                    </select>
                    <div className="text-xs space-y-1 text-dark-400">
                      <p className="text-amber-400 font-medium">⚠️ Getting "invalid signature" errors? Change this!</p>
                      <p><span className="text-white">Type 0:</span> Never logged into Polymarket.com, pay own gas</p>
                      <p><span className="text-white">Type 1:</span> Magic/email wallet export</p>
                      <p><span className="text-white">Type 2:</span> Logged into Polymarket.com with MetaMask/Phantom <span className="text-emerald-400">(Most common)</span></p>
                    </div>
                  </div>

                  {/* Wallet Balances & Withdraw */}
                  <div className="p-4 bg-dark-800 rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-primary-400" />
                        Wallet Balances
                      </h3>
                      <button
                        onClick={fetchWalletBalances}
                        disabled={isLoadingBalances}
                        className="text-xs text-dark-400 hover:text-white flex items-center gap-1"
                      >
                        <RefreshCw className={`w-3 h-3 ${isLoadingBalances ? 'animate-spin' : ''}`} />
                        Refresh
                      </button>
                    </div>
                    
                    {isLoadingBalances ? (
                      <div className="text-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin mx-auto text-primary-400" />
                      </div>
                    ) : walletBalances ? (
                      <div className="space-y-3">
                        {/* Signer Balance */}
                        <div className="p-3 bg-dark-700/50 rounded-lg">
                          <p className="text-xs text-dark-400 mb-1">Signer Wallet</p>
                          <p className="text-xs font-mono text-dark-500 mb-2">{walletBalances.signer?.address}</p>
                          <div className="flex gap-4 text-sm">
                            <span className="text-emerald-400">${walletBalances.signer?.usdc?.toFixed(2) || '0.00'} USDC</span>
                            <span className="text-purple-400">{walletBalances.signer?.pol?.toFixed(4) || '0'} POL</span>
                          </div>
                        </div>
                        
                        {/* Proxy Balance (if different) */}
                        {walletBalances.proxy?.isDifferent && (
                          <div className="p-3 bg-dark-700/50 rounded-lg">
                            <p className="text-xs text-dark-400 mb-1">Proxy Wallet (Polymarket)</p>
                            <p className="text-xs font-mono text-dark-500 mb-2">{walletBalances.proxy?.address}</p>
                            <div className="flex gap-4 text-sm">
                              <span className="text-emerald-400">${walletBalances.proxy?.usdc?.toFixed(2) || '0.00'} USDC</span>
                              <span className="text-purple-400">{walletBalances.proxy?.pol?.toFixed(4) || '0'} POL</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Withdraw Section (only if proxy has USDC) */}
                        {walletBalances.proxy?.isDifferent && walletBalances.proxy?.usdc > 0 && (
                          <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg space-y-3">
                            <div className="flex items-center gap-2 text-amber-400 text-sm">
                              <ArrowDownToLine className="w-4 h-4" />
                              <span className="font-medium">Withdraw from Proxy</span>
                            </div>
                            <p className="text-xs text-dark-400">
                              Move USDC from your Polymarket proxy wallet to your signer wallet.
                            </p>
                            <div className="flex gap-2">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                max={walletBalances.proxy?.usdc || 0}
                                value={withdrawAmount}
                                onChange={(e) => setWithdrawAmount(e.target.value)}
                                placeholder="Amount"
                                className="input flex-1"
                              />
                              <button
                                onClick={() => setWithdrawAmount(walletBalances.proxy?.usdc?.toFixed(2) || '0')}
                                className="btn btn-secondary text-xs"
                              >
                                Max
                              </button>
                              <button
                                onClick={handleWithdraw}
                                disabled={isWithdrawing || !withdrawAmount}
                                className="btn btn-primary"
                              >
                                {isWithdrawing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Withdraw'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-dark-500 text-center py-2">Click refresh to load balances</p>
                    )}
                  </div>

                  {/* WCOL (Wrapped Collateral) Banner */}
                  {wcolBalance?.hasWcol && (
                    <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-purple-400">
                          <DollarSign className="w-5 h-5" />
                          <span className="font-medium">Wrapped Collateral (WCOL) Detected</span>
                        </div>
                        <button
                          onClick={fetchWcolBalance}
                          disabled={isLoadingWcol}
                          className="text-xs text-dark-400 hover:text-white flex items-center gap-1"
                        >
                          <RefreshCw className={`w-3 h-3 ${isLoadingWcol ? 'animate-spin' : ''}`} />
                        </button>
                      </div>
                      <p className="text-sm text-dark-300">
                        You have <span className="text-purple-300 font-semibold">${wcolBalance.balance?.toFixed(6)}</span> WCOL stuck in your wallet.
                        This is wrapped USDC from redeemed positions that needs to be unwrapped.
                      </p>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={handleUnwrapWcol}
                          disabled={isUnwrappingWcol}
                          className="btn btn-primary flex items-center gap-2"
                        >
                          {isUnwrappingWcol ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <ArrowDownToLine className="w-4 h-4" />
                          )}
                          Unwrap to USDC
                        </button>
                        <a
                          href={`https://polygonscan.com/address/${wcolBalance.contractAddress}#writeContract`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-dark-400 hover:text-purple-400 flex items-center gap-1"
                        >
                          Manual via Polygonscan <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  )}

                  {walletSuccess && (
                    <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400 text-sm flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      {walletSuccess}
                    </div>
                  )}

                  {walletError && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {walletError}
                    </div>
                  )}
                  
                  <button
                    onClick={handleDisconnectWallet}
                    className="btn btn-danger"
                  >
                    Disconnect Wallet
                  </button>
                </div>
              ) : (
                <form onSubmit={handleConnectWallet} className="space-y-4">
                  {walletError && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {walletError}
                    </div>
                  )}
                  
                  {walletSuccess && (
                    <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400 text-sm flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      {walletSuccess}
                    </div>
                  )}

                  <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg mb-4">
                    <p className="text-cyan-300 text-sm font-medium mb-2">📋 How to find your addresses:</p>
                    <ol className="text-xs text-cyan-400/80 space-y-1 list-decimal list-inside">
                      <li><strong>Funder/Proxy</strong> → The address that holds your Polymarket collateral (Type 1/2 use a proxy/funder)</li>
                      <li><strong>Signer</strong> → The address that matches your private key (the one used to sign)</li>
                      <li>Type 0: signer=funder. Type 1/2: signer and funder are usually different.</li>
                    </ol>
                  </div>

                  <div>
                    <label className="label">{proxyLabel}</label>
                    <input
                      type="text"
                      value={proxyAddress}
                      onChange={(e) => setProxyAddress(e.target.value)}
                      className="input font-mono"
                      placeholder="0x..."
                      required={proxyRequired}
                    />
                    <p className="text-xs text-dark-500 mt-1">
                      {proxyRequired
                        ? (<><span className="text-primary-400 font-medium">Required</span> for Type 2. This is the Polymarket proxy/funder address (the one shown on your profile).</>)
                        : (<>Optional for Type 0/1. If blank, we will auto-derive it for Type 1 and use signer=funder for Type 0.</>)}
                    </p>
                  </div>

                  <div>
                    <label className="label">Signer/Wallet Address</label>
                    <input
                      type="text"
                      value={walletAddress}
                      onChange={(e) => setWalletAddress(e.target.value)}
                      className="input font-mono"
                      placeholder="0x..."
                      required
                    />
                    <p className="text-xs text-dark-500 mt-1">
                      The address from your <strong className="text-primary-400">private key</strong> (MetaMask, exported Magic wallet, etc.)
                    </p>
                  </div>

                  <div>
                    <label className="label">Private Key</label>
                    <div className="relative">
                      <input
                        type={showPrivateKey ? 'text' : 'password'}
                        value={privateKey}
                        onChange={(e) => setPrivateKey(e.target.value)}
                        className="input font-mono pr-10"
                        placeholder="0x..."
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPrivateKey(!showPrivateKey)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 hover:text-white"
                      >
                        {showPrivateKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-dark-500 mt-1">
                      Your private key is encrypted and stored securely
                    </p>
                  </div>

                  <div>
                    <label className="label">Signature Type</label>
                    <select
                      value={signatureType}
                      onChange={(e) => setSignatureType(parseInt(e.target.value))}
                      className="input"
                    >
                      <option value={0}>Type 0 - Direct Wallet (Never used Polymarket.com)</option>
                      <option value={1}>Type 1 - Magic/Email Wallet Export</option>
                      <option value={2}>Type 2 - MetaMask/Phantom on Polymarket.com (Most Common)</option>
                    </select>
                    <div className="mt-2 p-3 bg-dark-800 rounded-lg text-xs space-y-3">
                      <p className="text-amber-400 font-medium">⚠️ Getting "invalid signature" errors? Try changing this!</p>
                      
                      <div className={`p-2 rounded border ${signatureType === 0 ? 'bg-primary-500/10 border-primary-500/30' : 'bg-dark-700/50 border-dark-600'}`}>
                        <p className="text-white font-medium">Type 0 - Direct Wallet</p>
                        <p className="text-dark-300 mt-1">Never logged into polymarket.com. You set up CLOB manually and pay your own MATIC gas.</p>
                        <p className="text-cyan-400 mt-1">→ Both addresses are the <strong>SAME</strong> (your MetaMask address)</p>
                      </div>
                      
                      <div className={`p-2 rounded border ${signatureType === 1 ? 'bg-primary-500/10 border-primary-500/30' : 'bg-dark-700/50 border-dark-600'}`}>
                        <p className="text-white font-medium">Type 1 - Magic/Email Export</p>
                        <p className="text-dark-300 mt-1">You logged into Polymarket with email and exported your private key.</p>
                        <p className="text-cyan-400 mt-1">→ Signer is your exported key, but collateral/trading can live in a <strong>derived proxy/funder</strong></p>
                      </div>
                      
                      <div className={`p-2 rounded border ${signatureType === 2 ? 'bg-primary-500/10 border-primary-500/30' : 'bg-dark-700/50 border-dark-600'}`}>
                        <p className="text-white font-medium">Type 2 - Polymarket.com Login <span className="text-emerald-400">(Most Common)</span></p>
                        <p className="text-dark-300 mt-1">You logged into polymarket.com with MetaMask/Phantom/etc.</p>
                        <p className="text-cyan-400 mt-1">→ Addresses are <strong>DIFFERENT</strong>:</p>
                        <p className="text-cyan-400 ml-3">• Polymarket Profile = The 0x address on polymarket.com</p>
                        <p className="text-cyan-400 ml-3">• Signer = Your MetaMask/Phantom address</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-200">
                        <p className="font-medium mb-1">Security Notice</p>
                        <p className="text-amber-300/80">
                          Your private key is encrypted before storage. Never share your private key with anyone.
                          Consider using a dedicated trading wallet with limited funds.
                        </p>
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isConnecting}
                    className="btn btn-primary"
                  >
                    {isConnecting ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Connecting...
                      </span>
                    ) : (
                      'Connect Wallet'
                    )}
                  </button>
                </form>
              )}

              <div className="pt-4 border-t border-dark-700">
                <h3 className="font-medium mb-2">Need help?</h3>
                <ul className="text-sm text-dark-400 space-y-1">
                  <li>
                    <a
                      href="https://docs.polymarket.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-400 hover:text-primary-300 flex items-center gap-1"
                    >
                      Polymarket Documentation <ExternalLink className="w-3 h-3" />
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://polymarket.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-400 hover:text-primary-300 flex items-center gap-1"
                    >
                      Get your wallet address <ExternalLink className="w-3 h-3" />
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Trading Tab */}
          {activeTab === 'trading' && (
            <div className="card space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-1">Trading Settings</h2>
                <p className="text-sm text-dark-400">
                  Configure default trading parameters
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Max Position Size ($)</label>
                  <input
                    type="number"
                    value={settings.maxPositionSize}
                    onChange={(e) => setSettings({
                      ...settings,
                      maxPositionSize: parseFloat(e.target.value)
                    })}
                    className="input"
                    min="1"
                  />
                  <p className="text-xs text-dark-500 mt-1">
                    Maximum size for any single position
                  </p>
                </div>

                <div>
                  <label className="label">Max Total Exposure ($)</label>
                  <input
                    type="number"
                    value={settings.maxTotalExposure}
                    onChange={(e) => setSettings({
                      ...settings,
                      maxTotalExposure: parseFloat(e.target.value)
                    })}
                    className="input"
                    min="1"
                  />
                  <p className="text-xs text-dark-500 mt-1">
                    Maximum total value across all positions
                  </p>
                </div>

                <div>
                  <label className="label">Default Slippage (%)</label>
                  <input
                    type="number"
                    value={settings.defaultSlippage}
                    onChange={(e) => setSettings({
                      ...settings,
                      defaultSlippage: parseFloat(e.target.value)
                    })}
                    className="input"
                    min="0"
                    max="100"
                    step="0.1"
                  />
                  <p className="text-xs text-dark-500 mt-1">
                    Maximum acceptable slippage for trades
                  </p>
                </div>

                <div className="flex items-center gap-3 pt-6">
                  <input
                    type="checkbox"
                    id="autoCompound"
                    checked={settings.autoCompound}
                    onChange={(e) => setSettings({
                      ...settings,
                      autoCompound: e.target.checked
                    })}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500"
                  />
                  <label htmlFor="autoCompound" className="text-sm">
                    Auto-compound profits
                  </label>
                </div>
              </div>

              <button
                onClick={handleSaveSettings}
                disabled={isSaving}
                className="btn btn-primary"
              >
                {isSaving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="card space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-1">Notification Preferences</h2>
                <p className="text-sm text-dark-400">
                  Choose how you want to be notified
                </p>
              </div>

              <div className="space-y-4">
                {[
                  { key: 'email', label: 'Email Notifications', desc: 'Receive updates via email' },
                  { key: 'browser', label: 'Browser Notifications', desc: 'Push notifications in browser' },
                  { key: 'trades', label: 'Trade Alerts', desc: 'Notify when trades are executed' },
                  { key: 'stopLoss', label: 'Stop Loss Alerts', desc: 'Notify when stop loss triggers' }
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between p-4 bg-dark-700/50 rounded-lg">
                    <div>
                      <p className="font-medium">{item.label}</p>
                      <p className="text-sm text-dark-400">{item.desc}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.notifications[item.key]}
                        onChange={(e) => setSettings({
                          ...settings,
                          notifications: {
                            ...settings.notifications,
                            [item.key]: e.target.checked
                          }
                        })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-dark-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                ))}
              </div>

              <button
                onClick={handleSaveSettings}
                disabled={isSaving}
                className="btn btn-primary"
              >
                {isSaving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="card space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-1">Security Settings</h2>
                <p className="text-sm text-dark-400">
                  Manage your account security
                </p>
              </div>

              <div className="space-y-4">
                <div className="p-4 bg-dark-700/50 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <Key className="w-5 h-5 text-primary-400" />
                    <h3 className="font-medium">Change Password</h3>
                  </div>
                  <p className="text-sm text-dark-400 mb-4">
                    Update your account password
                  </p>
                  <button 
                    onClick={() => setShowPasswordModal(true)}
                    className="btn btn-secondary"
                  >
                    Change Password
                  </button>
                </div>

                <div className="p-4 bg-dark-700/50 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <Shield className="w-5 h-5 text-primary-400" />
                    <h3 className="font-medium">Two-Factor Authentication</h3>
                  </div>
                  <p className="text-sm text-dark-400 mb-4">
                    Add an extra layer of security to your account
                  </p>
                  <button 
                    onClick={() => alert('Two-factor authentication coming soon!')}
                    className="btn btn-secondary"
                  >
                    Enable 2FA
                  </button>
                </div>

                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    <h3 className="font-medium text-red-400">Danger Zone</h3>
                  </div>
                  <p className="text-sm text-dark-400 mb-4">
                    Permanently delete your account and all data
                  </p>
                  <button 
                    onClick={handleDeleteAccount}
                    className="btn btn-danger"
                  >
                    Delete Account
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Change Password</h3>
            <div className="space-y-4">
              <div>
                <label className="label">Current Password</label>
                <input
                  type="password"
                  value={passwordData.current}
                  onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">New Password</label>
                <input
                  type="password"
                  value={passwordData.new}
                  onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Confirm New Password</label>
                <input
                  type="password"
                  value={passwordData.confirm}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                  className="input"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordData({ current: '', new: '', confirm: '' });
                  }}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleChangePassword}
                  disabled={isChangingPassword}
                  className="btn btn-primary flex-1"
                >
                  {isChangingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
